/**
 * RS Customizer — Alpha Tote
 * Visual preview renderer for alpha tote customization.
 * Fetches metaobject data (backgrounds, overlays, letter positioning settings),
 * injects a gallery preview slide, and renders font-based letter initials
 * as the user types — with fabric texture fill and per-variant color.
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const LIQUID_VIEW = 'metaobject-alpha-tote';

  const FONT_FAMILIES = {
    twoInitials: "'TwoInitialsFont', 'Arial Black', Arial, sans-serif",
    threeInitials: "'ThreeInitialsFont', 'Arial Black', Arial, sans-serif",
  };

  let _injectedSlideId = null;
  let _metaobjectData = null;
  let _backgroundsData = null;
  let _currentVariantTitle = null;
  let _currentToteSize = null;
  let _selectedColor = null;

  // ─── DOM helpers ────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) {
      text += field.value;
    });
    return text.trim().toUpperCase().replace(/[^A-Z]/g, '');
  }

  // ─── Data fetching ──────────────────────────────────────────────

  async function _fetchBaseConfig() {
    const response = await fetch(
      window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW
    );
    return response.json();
  }

  // ─── Background / overlay management ────────────────────────────

  function _getBackgroundForVariant(toteSize) {
    if (!_backgroundsData || !toteSize) return null;
    return _backgroundsData[toteSize] || null;
  }

  function _getToteSizeForVariant(variantTitle) {
    if (!_metaobjectData || !variantTitle) return null;

    const productTitle = _metaobjectData.product_title || '';
    const variantKey = productTitle + ' - ' + variantTitle;
    const variantData = _metaobjectData.customizer_data
      ? _metaobjectData.customizer_data[variantKey]
      : null;

    return variantData ? variantData.tote_size : null;
  }

  // ─── Gallery content container ──────────────────────────────────

  function _getContentContainer() {
    if (!_injectedSlideId) return null;

    const slide = document.querySelector(
      '[data-rs-injected="' + _injectedSlideId + '"]'
    );
    if (!slide) return null;

    const customizerView = slide.querySelector('.customizer-view');
    if (!customizerView) return null;

    let canvas = customizerView.querySelector('.rs-alpha__canvas');
    if (!canvas) {
      canvas = document.createElement('div');
      canvas.className = 'rs-alpha__canvas';
      customizerView.appendChild(canvas);
    }

    return canvas;
  }

  // ─── Color management ──────────────────────────────────────────

  function _hex2rgb(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { r: r, g: g, b: b };
  }

  function _applyColorCssVars(color) {
    if (!color) return;
    const rgb = _hex2rgb(color);
    document.documentElement.style.setProperty(
      '--customizer-alpha-tote-selected-color',
      color
    );
    document.documentElement.style.setProperty(
      '--customizer-alpha-tote-r',
      rgb.r
    );
    document.documentElement.style.setProperty(
      '--customizer-alpha-tote-g',
      rgb.g
    );
    document.documentElement.style.setProperty(
      '--customizer-alpha-tote-b',
      rgb.b
    );
  }

  function _detectColorForVariant(variantTitle) {
    if (!_metaobjectData || !_metaobjectData.color_names || !variantTitle)
      return;

    // Alpha-tote variant titles are "Color / Size" (e.g. "Turquoise / Small").
    // Extract the color portion (first part before " / ").
    const parts = variantTitle.split(' / ');
    const colorPart = (parts[0] || '').trim();
    if (!colorPart) return;

    let match = _metaobjectData.color_names.find(function (item) {
      return (
        item.key === colorPart ||
        item.name === colorPart ||
        item.key.toLowerCase() === colorPart.toLowerCase() ||
        item.name.toLowerCase() === colorPart.toLowerCase()
      );
    });

    // Fallback: try matching against checked radio in DOM
    if (!match) {
      const sectionEl = document.getElementById(
        'MainProduct--' + window.RSProduct.sectionId
      );
      if (sectionEl) {
        const checkedRadio = sectionEl.querySelector(
          'input[type="radio"]:checked'
        );
        if (checkedRadio) {
          const radioVal = (checkedRadio.value || '').trim();
          if (radioVal) {
            match = _metaobjectData.color_names.find(function (item) {
              return item.key === radioVal || item.name === radioVal;
            });
          }
        }
      }
    }

    if (match && match.color) {
      _selectedColor = match.color;
      _applyColorCssVars(_selectedColor);
    }
  }

  // ─── Letter rendering ──────────────────────────────────────────

  function _renderLetters() {
    const text = _getMtoText();
    const container = _getContentContainer();
    if (!container) return;

    container
      .querySelectorAll('[data-custom-text-charm]')
      .forEach(function (el) { el.remove(); });

    if (!text || text.length < 2 || text.length > 3) return;

    const letterCount = text.length;
    const settingsKey = letterCount === 2 ? 'two_letters' : 'three_letters';

    const sizeSettings = _metaobjectData.settings
      ? _metaobjectData.settings[_currentToteSize]
      : null;
    if (!sizeSettings) return;

    const positionSettings = sizeSettings[settingsKey];
    if (!positionSettings) return;

    const letterKeys = Object.keys(positionSettings);
    const color = _selectedColor || '#000000';

    letterKeys.forEach(function (letterKey, i) {
      if (i < text.length) {
        _createLetterElement(
          container,
          text[i],
          positionSettings[letterKey],
          i,
          letterCount,
          color
        );
      }
    });
  }

  function _createLetterElement(
    container,
    letterChar,
    settings,
    index,
    totalLetters,
    color
  ) {
    const el = document.createElement('div');
    el.setAttribute('data-custom-text-charm', String(index));
    el.className = 'rs-alpha__letter';
    el.textContent = letterChar;

    el.style.fontFamily =
      totalLetters === 2
        ? FONT_FAMILIES.twoInitials
        : FONT_FAMILIES.threeInitials;
    el.style.fontSize = '100px';
    el.style.fontWeight = 'normal';
    el.style.color = color;

    if (totalLetters === 2) {
      el.classList.add('rs-alpha__letter--two');
    } else {
      el.classList.add('rs-alpha__letter--three');
    }

    const centerX = settings.x_position + settings.width / 2;
    const centerY = settings.y_position + settings.height / 2;
    el.style.top = centerY + '%';
    el.style.left = centerX + '%';
    el.style.transform = 'translate(-50%, -50%)';

    container.appendChild(el);

    // Force reflow then scale to fit allocated area
    void el.offsetWidth;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width || 800;
    const containerHeight = containerRect.height || 600;

    const allocatedWidth = (settings.width / 100) * containerWidth;
    const allocatedHeight = (settings.height / 100) * containerHeight;

    const textRect = el.getBoundingClientRect();
    const scaleX = allocatedWidth / textRect.width;
    const scaleY = allocatedHeight / textRect.height;

    el.style.transform =
      'translate(-50%, -50%) scaleX(' + scaleX + ') scaleY(' + scaleY + ')';

    el.setAttribute('data-stored-x', settings.x_position);
    el.setAttribute('data-stored-y', settings.y_position);
    el.setAttribute('data-stored-w', settings.width);
    el.setAttribute('data-stored-h', settings.height);
    el.setAttribute('data-total-letters', totalLetters);
  }

  // ─── Overlay rendering ─────────────────────────────────────────

  function _renderOverlay() {
    const container = _getContentContainer();
    if (!container) return;

    const existing = container.querySelector('.rs-alpha__overlay');
    if (existing) existing.remove();

    if (
      !_currentToteSize ||
      !_metaobjectData ||
      !_metaobjectData.overlays
    )
      return;

    const overlayUrl = _metaobjectData.overlays[_currentToteSize];
    if (!overlayUrl) return;

    const overlay = document.createElement('div');
    overlay.className = 'rs-alpha__overlay';

    const img = document.createElement('img');
    img.src = overlayUrl;
    img.alt = 'Customizer Overlay';
    overlay.appendChild(img);

    container.appendChild(overlay);
  }

  // ─── Resize handling ───────────────────────────────────────────

  function _handleResize() {
    const letterEls = document.querySelectorAll('[data-custom-text-charm]');
    letterEls.forEach(function (el) {
      const canvas = el.closest('.rs-alpha__canvas');
      if (!canvas) return;

      const storedX = parseFloat(el.getAttribute('data-stored-x'));
      const storedY = parseFloat(el.getAttribute('data-stored-y'));
      const storedW = parseFloat(el.getAttribute('data-stored-w'));
      const storedH = parseFloat(el.getAttribute('data-stored-h'));

      if (isNaN(storedX)) return;

      const centerX = storedX + storedW / 2;
      const centerY = storedY + storedH / 2;
      el.style.top = centerY + '%';
      el.style.left = centerX + '%';
      el.style.transform = 'translate(-50%, -50%)';

      void el.offsetWidth;

      const canvasRect = canvas.getBoundingClientRect();
      const allocatedWidth = (storedW / 100) * (canvasRect.width || 800);
      const allocatedHeight = (storedH / 100) * (canvasRect.height || 600);

      const textRect = el.getBoundingClientRect();
      const scaleX = allocatedWidth / textRect.width;
      const scaleY = allocatedHeight / textRect.height;

      el.style.transform =
        'translate(-50%, -50%) scaleX(' +
        scaleX +
        ') scaleY(' +
        scaleY +
        ')';
    });
  }

  let _resizeTimer = null;
  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(_handleResize, 250);
  }

  // ─── Event handlers ────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _renderLetters();
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleVariantChange(event) {
    if (
      !event.data ||
      event.data.sectionId !== window.RSProduct.sectionId
    )
      return;

    const variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;
    _currentToteSize = _getToteSizeForVariant(_currentVariantTitle);

    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const backgroundUrl = _getBackgroundForVariant(_currentToteSize);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    _detectColorForVariant(_currentVariantTitle);
    _renderOverlay();

    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
      if (_injectedSlideId) {
        window.RSProduct.gallery.navigate(_injectedSlideId);
      }
    }
  }

  function _handleColorRadioChange(event) {
    const input = event.target;
    if (
      !input.matches ||
      !input.matches('input[type="radio"]') ||
      !_metaobjectData ||
      !_metaobjectData.color_names
    )
      return;

    const sectionEl = document.getElementById(
      'MainProduct--' + window.RSProduct.sectionId
    );
    if (!sectionEl || !sectionEl.contains(input)) return;

    const colorKey = (input.value || '').trim();
    if (!colorKey) return;

    const match = _metaobjectData.color_names.find(function (item) {
      return item.key === colorKey || item.name === colorKey;
    });

    if (match && match.color) {
      _selectedColor = match.color;
      _applyColorCssVars(_selectedColor);

      document.querySelectorAll('[data-custom-text-charm]').forEach(function (el) {
        el.style.color = _selectedColor;
      });
    }
  }

  // ─── Initialization ────────────────────────────────────────────

  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    let baseConfig;
    try {
      baseConfig = await _fetchBaseConfig();
    } catch (err) {
      console.error('[AlphaTote] Data fetch failed:', err);
      return;
    }

    _metaobjectData = baseConfig;
    _backgroundsData = baseConfig.backgrounds || {};

    const initialVariantId = String(
      window.RSProduct.config.initialVariantId || ''
    );
    const variantNames = baseConfig.variant_names || {};
    const fullName = variantNames[initialVariantId] || '';
    const parts = fullName.split(' - ');
    _currentVariantTitle =
      parts.length > 1 ? parts[parts.length - 1] : fullName;
    _currentToteSize = _getToteSizeForVariant(_currentVariantTitle);

    const backgroundUrl = _getBackgroundForVariant(_currentToteSize);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    _detectColorForVariant(_currentVariantTitle);
    _renderOverlay();

    document.addEventListener('input', _handleInput);
    document.addEventListener('focusin', _handleInputFocus);
    document.addEventListener('change', _handleColorRadioChange);
    window.addEventListener('resize', _onResize);

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(
        window.theme.PUB_SUB_EVENTS.variantChange,
        _handleVariantChange
      );
    }

    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.RSProduct) return;
    _init();
  });
})();
